// Thin wrapper around @mapbox/mapbox-gl-draw that exposes the shapes we
// actually need. Isolates the Draw library so the Course Map can treat it
// as a black box.

import MapboxDraw from '@mapbox/mapbox-gl-draw';
import type { Map as MapboxMap } from 'mapbox-gl';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

export interface PolygonEditorHandle {
  start: (initial?: GeoJSON.Polygon | GeoJSON.MultiPolygon) => void;
  cancel: () => void;
  remove: () => void;
  current: () => GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
}

export function attachPolygonEditor(
  map: MapboxMap,
  onCommit: (polygon: GeoJSON.Polygon | GeoJSON.MultiPolygon) => void,
): PolygonEditorHandle {
  const draw = new MapboxDraw({
    displayControlsDefault: false,
    controls: {},
    defaultMode: 'simple_select',
    styles: DRAW_STYLES,
  });
  map.addControl(draw);

  const handleChange = () => {
    const fc = draw.getAll();
    const feature = fc.features[0];
    if (!feature) return;
    const geom = feature.geometry;
    if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
      onCommit(geom);
    }
  };

  map.on('draw.create', handleChange);
  map.on('draw.update', handleChange);

  return {
    start(initial) {
      draw.deleteAll();
      if (initial) {
        draw.add({ type: 'Feature', properties: {}, geometry: initial });
        draw.changeMode('simple_select');
      } else {
        draw.changeMode('draw_polygon');
      }
    },
    cancel() {
      draw.deleteAll();
      draw.changeMode('simple_select');
    },
    remove() {
      map.off('draw.create', handleChange);
      map.off('draw.update', handleChange);
      try {
        map.removeControl(draw);
      } catch {
        /* swallow — control already gone */
      }
    },
    current() {
      const fc = draw.getAll();
      const feature = fc.features[0];
      if (!feature) return null;
      const geom = feature.geometry;
      return geom.type === 'Polygon' || geom.type === 'MultiPolygon' ? geom : null;
    },
  };
}

// GreenIQ-branded draw styles — forest strokes, gold active verts.
const DRAW_STYLES = [
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill' as const,
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    paint: { 'fill-color': '#22A060', 'fill-outline-color': '#14532D', 'fill-opacity': 0.35 },
  },
  {
    id: 'gl-draw-polygon-stroke',
    type: 'line' as const,
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': '#14532D', 'line-width': 2 },
  },
  {
    id: 'gl-draw-vertex',
    type: 'circle' as const,
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
    paint: { 'circle-radius': 5, 'circle-color': '#B8A372', 'circle-stroke-width': 2, 'circle-stroke-color': '#FAF9F5' },
  },
  {
    id: 'gl-draw-midpoint',
    type: 'circle' as const,
    filter: ['all', ['==', 'meta', 'midpoint'], ['==', '$type', 'Point']],
    paint: { 'circle-radius': 3, 'circle-color': '#B8A372' },
  },
  {
    id: 'gl-draw-line-active',
    type: 'line' as const,
    filter: ['all', ['==', '$type', 'LineString'], ['==', 'active', 'true']],
    paint: { 'line-color': '#22A060', 'line-width': 2, 'line-dasharray': [2, 2] },
  },
];
